'use client';

import React from 'react';
import {
  MixerHorizontalIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
  PersonIcon,
} from '@radix-ui/react-icons';

import {
  Box,
  BoxProps,
  Stack,
  Button,
  IconButton,
  Checkbox,
  Input,
  Badge,
  Separator,
  Text,
  Popover,
} from '../../index';

export type ActivityType =
  | 'create'
  | 'update'
  | 'delete'
  | 'comment'
  | 'status'
  | 'assignment'
  | 'system'
  | 'security';

export interface ActivityFilterOptions {
  types?: Array<ActivityType>;
  actors?: Array<{ id: string; name: string }>;
  dateRange?: {
    start?: Date;
    end?: Date;
  };
  search?: string;
  targets?: Array<{ type: string; id: string; name: string }>;
}

export interface ActivityFilterProps extends Omit<BoxProps, 'onChange'> {
  /** Current filter values */
  filters: ActivityFilterOptions;
  /** Available filter options */
  availableFilters?: {
    types?: Array<{ value: string; label: string }>;
    actors?: Array<{ id: string; name: string; email?: string; avatar?: string }>;
    targetTypes?: Array<{ value: string; label: string }>;
  };
  /** Filter change handler */
  onChange: (filters: ActivityFilterOptions) => void;
  /** Whether to show search input */
  showSearch?: boolean;
  /** Whether to show quick filters */
  showQuickFilters?: boolean;
  /** Whether filters are being applied */
  loading?: boolean;
  /** Compact mode */
  compact?: boolean;
}

/**
 * ActivityFilter component - provides filtering controls for activity feeds
 * Supports multiple filter types with popover advanced options
 */
export const ActivityFilter: React.FC<ActivityFilterProps> = ({
  filters,
  availableFilters = {},
  onChange,
  showSearch = true,
  showQuickFilters = true,
  loading = false,
  compact = false,
  ...boxProps
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [localFilters, setLocalFilters] = React.useState<ActivityFilterOptions>(filters);

  // Update local filters when prop changes
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const defaultTypes = availableFilters.types || [
    { value: 'create', label: 'Oluşturma' },
    { value: 'update', label: 'Güncelleme' },
    { value: 'delete', label: 'Silme' },
    { value: 'comment', label: 'Yorum' },
    { value: 'status', label: 'Durum' },
    { value: 'assignment', label: 'Atama' },
    { value: 'system', label: 'Sistem' },
    { value: 'security', label: 'Güvenlik' },
  ];

  // Count active filters
  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (localFilters.types && localFilters.types.length > 0) count++;
    if (localFilters.actors && localFilters.actors.length > 0) count++;
    if (localFilters.dateRange?.start || localFilters.dateRange?.end) count++;
    if (localFilters.search) count++;
    if (localFilters.targets && localFilters.targets.length > 0) count++;
    return count;
  }, [localFilters]);

  // Handle type toggle
  const handleTypeToggle = (type: ActivityType) => {
    const currentTypes = localFilters.types || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];

    setLocalFilters({ ...localFilters, types: newTypes });
  };

  // Handle actor toggle
  const handleActorToggle = (actorId: string) => {
    const currentActors = localFilters.actors || [];
    const actor = availableFilters.actors?.find((a) => a.id === actorId);
    if (!actor) return;

    const isSelected = currentActors.some((a) => a.id === actorId);
    const newActors = isSelected
      ? currentActors.filter((a) => a.id !== actorId)
      : [...currentActors, { id: actor.id, name: actor.name }];

    setLocalFilters({ ...localFilters, actors: newActors });
  };

  // Apply filters
  const applyFilters = () => {
    onChange(localFilters);
    setIsOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    const emptyFilters: ActivityFilterOptions = {};
    setLocalFilters(emptyFilters);
    onChange(emptyFilters);
  };

  // Quick filter buttons
  const quickFilters = [
    { type: 'create', label: 'Oluşturmalar' },
    { type: 'update', label: 'Güncellemeler' },
    { type: 'comment', label: 'Yorumlar' },
    { type: 'status', label: 'Durum Değişiklikleri' },
  ];

  return (
    <Box {...boxProps}>
      <Stack gap={3}>
        {/* Search and filter button row */}
        <Box display="flex" gap={2} alignItems="center">
          {/* Search input */}
          {showSearch && (
            <Box flex={1}>
              <Input
                placeholder="Aktivitelerde ara..."
                value={localFilters.search || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newFilters = { ...localFilters, search: e.target.value };
                  setLocalFilters(newFilters);
                  onChange(newFilters);
                }}
                leftIcon={<MagnifyingGlassIcon />}
                inputSize={compact ? 'sm' : 'md'}
              />
            </Box>
          )}

          {/* Advanced filters popover */}
          <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger asChild>
              <Button
                variant="outline"
                leftIcon={<MixerHorizontalIcon />}
                size={compact ? 'sm' : 'md'}
                onClick={() => setIsOpen(!isOpen)}
              >
                Filtreler
                {activeFilterCount > 0 && <Badge size="sm">{activeFilterCount}</Badge>}
              </Button>
            </Popover.Trigger>
            <Popover.Content width="350px">
              <Stack gap={4} padding={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Text fontWeight="semibold">Gelişmiş Filtreler</Text>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsOpen(false)}
                    aria-label="Kapat"
                    icon={<Cross2Icon />}
                  />
                </Box>

                <Separator />

                {/* Activity types */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" marginBottom={2}>
                    Aktivite Türleri
                  </Text>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    {defaultTypes.map((type) => (
                      <Checkbox
                        key={type.value}
                        checked={localFilters.types?.includes(type.value as ActivityType) || false}
                        onCheckedChange={() => handleTypeToggle(type.value as ActivityType)}
                      >
                        {type.label}
                      </Checkbox>
                    ))}
                  </Box>
                </Box>

                {/* Actors */}
                {availableFilters.actors && availableFilters.actors.length > 0 && (
                  <>
                    <Separator />
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" marginBottom={2}>
                        Kullanıcılar
                      </Text>
                      <Stack gap={2} maxHeight="200px" overflowY="auto">
                        {availableFilters.actors.map((actor) => (
                          <Checkbox
                            key={actor.id}
                            checked={localFilters.actors?.some((a) => a.id === actor.id) || false}
                            onCheckedChange={() => handleActorToggle(actor.id)}
                          >
                            <Box display="flex" alignItems="center" gap={2}>
                              <PersonIcon />
                              <Text fontSize="sm">{actor.name}</Text>
                            </Box>
                          </Checkbox>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}

                {/* Date range */}
                <Separator />
                <Box>
                  <Text fontSize="sm" fontWeight="medium" marginBottom={2}>
                    Tarih Aralığı
                  </Text>
                  <Stack gap={2}>
                    <Input
                      type="date"
                      inputSize="sm"
                      value={localFilters.dateRange?.start?.toISOString().split('T')[0] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const date = e.target.value ? new Date(e.target.value) : undefined;
                        setLocalFilters({
                          ...localFilters,
                          dateRange: { ...localFilters.dateRange, start: date },
                        });
                      }}
                      placeholder="Başlangıç tarihi"
                    />
                    <Input
                      type="date"
                      inputSize="sm"
                      value={localFilters.dateRange?.end?.toISOString().split('T')[0] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const date = e.target.value ? new Date(e.target.value) : undefined;
                        setLocalFilters({
                          ...localFilters,
                          dateRange: { ...localFilters.dateRange, end: date },
                        });
                      }}
                      placeholder="Bitiş tarihi"
                    />
                  </Stack>
                </Box>

                <Separator />

                {/* Actions */}
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={resetFilters}
                    disabled={activeFilterCount === 0}
                  >
                    Temizle
                  </Button>
                  <Button size="sm" variant="primary" onClick={applyFilters} disabled={loading}>
                    Uygula
                  </Button>
                </Box>
              </Stack>
            </Popover.Content>
          </Popover.Root>

          {/* Clear all filters */}
          {activeFilterCount > 0 && (
            <IconButton
              size={compact ? 'sm' : 'md'}
              variant="ghost"
              onClick={resetFilters}
              aria-label="Tüm filtreleri temizle"
              title="Tüm filtreleri temizle"
              icon={<Cross2Icon />}
            />
          )}
        </Box>

        {/* Quick filter buttons */}
        {showQuickFilters && (
          <Box display="flex" gap={2} flexWrap="wrap">
            {quickFilters.map((filter) => {
              const isActive = localFilters.types?.includes(filter.type as ActivityType);
              return (
                <Button
                  key={filter.type}
                  size="sm"
                  variant={isActive ? 'primary' : 'outline'}
                  onClick={() => {
                    const newFilters = {
                      ...localFilters,
                      types: isActive ? [] : [filter.type as ActivityType],
                    };
                    setLocalFilters(newFilters);
                    onChange(newFilters);
                  }}
                >
                  {filter.label}
                </Button>
              );
            })}
          </Box>
        )}

        {/* Active filters display */}
        {activeFilterCount > 0 && (
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Text fontSize="sm" color="fg.muted">
              Aktif filtreler:
            </Text>

            {localFilters.types && localFilters.types.length > 0 && (
              <Badge size="sm">{localFilters.types.length} tür</Badge>
            )}

            {localFilters.actors && localFilters.actors.length > 0 && (
              <Badge size="sm">{localFilters.actors.length} kullanıcı</Badge>
            )}

            {(localFilters.dateRange?.start || localFilters.dateRange?.end) && (
              <Badge size="sm">Tarih aralığı</Badge>
            )}

            {localFilters.search && <Badge size="sm">Arama: {localFilters.search}</Badge>}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ActivityFilter;
